import React, { useRef, useState } from 'react';
import '../styles/contact.css';
import SEO from '../components/SEO';

const API_BASE_URL = process.env.REACT_APP_OPENWEB_API_BASE_URL;

const PRODUCT_OPTIONS = [
  { value: '', label: 'Select a product' },
  { value: 'SupplyMD', label: 'SupplyMD — healthcare inventory' },
  { value: 'OpenRFQ', label: 'OpenRFQ — supplier bidding' },
  { value: 'AInventory', label: 'AInventory — AI inventory intelligence' },
  { value: 'ATracker', label: 'ATracker — mobile asset tracking' },
  { value: 'Platform', label: 'The full OpenUse platform' },
];

const TEAM_SIZE_OPTIONS = [
  { value: '', label: 'Select team size' },
  { value: '1-50', label: '1–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-1000', label: '201–1,000' },
  { value: '1000+', label: '1,000+' },
];

// Curated role list keeps sales-qualifying data clean and free of typos.
const ROLE_OPTIONS = [
  { value: '', label: 'Select your role' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Procurement / Purchasing', label: 'Procurement / Purchasing' },
  { value: 'Inventory Management', label: 'Inventory Management' },
  { value: 'Supply Chain', label: 'Supply Chain' },
  { value: 'Warehouse / Logistics', label: 'Warehouse / Logistics' },
  { value: 'Asset / Equipment Management', label: 'Asset / Equipment Management' },
  { value: 'Sourcing', label: 'Sourcing' },
  { value: 'Healthcare / Clinic Operations', label: 'Healthcare / Clinic Operations' },
  { value: 'Facilities Management', label: 'Facilities Management' },
  { value: 'Business Owner / General Management', label: 'Business Owner / General Management' },
  { value: 'Other', label: 'Other' },
];

const RequestDemo: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [productInterest, setProductInterest] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot: hidden field bots tend to fill in. Backend rejects any non-empty value.
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Captured at mount so the backend can reject unrealistically fast submissions.
  const formLoadedAt = useRef<number>(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');

    const url = `${API_BASE_URL}/api/contact?timestamp=${new Date().getTime()}`;
    // Compose a structured message so the existing /api/contact endpoint can
    // capture the new sales-qualifying fields without a backend change.
    const composedMessage = [
      `Company: ${company}`,
      `Role: ${role}`,
      `Product interest: ${productInterest}`,
      `Team size: ${teamSize}`,
      '',
      message,
    ].join('\n');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message: composedMessage,
          company_website: companyWebsite,
          formLoadedAt: formLoadedAt.current,
        }),
      });
      if (response.ok) {
        // Use a full browser navigation (not React Router's client-side
        // navigate) so Google Tag Manager fires a real Page View on the
        // thank-you page. The Google Ads Conversion Tracking trigger is
        // configured as Page View → Page URL contains /request-demo/thank-you,
        // which only fires on real page loads.
        window.location.assign('/request-demo/thank-you');
      } else {
        setErrorMessage('We could not submit your request. Please try again or email sales@openuse.com.');
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Network error. Please check your connection or email sales@openuse.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Request a Demo"
        description="Tell us about your team and we'll match you with the right OpenUse product specialist — SupplyMD, OpenRFQ, AInventory, or ATracker."
        path="/request-demo"
      />
      <div className="contact-intro">
        <p className="hero-eyebrow">Talk to sales</p>
        <h1>Request a demo</h1>
        <p>
          Tell us a bit about your team and the operational problem you&rsquo;re
          trying to solve. We&rsquo;ll match you with the right product
          specialist and follow up within one business day.
        </p>
      </div>

      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <form onSubmit={handleSubmit} className="contact-form">
        {/* Honeypot — hidden from humans, irresistible to bots. */}
        <div className="hp-field" aria-hidden="true">
          <label htmlFor="company_website">Company website</label>
          <input
            id="company_website"
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
          />
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="name">Name</label>
            <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-field">
            <label htmlFor="email">Work email</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="company">Company</label>
            <input id="company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} required />
          </div>
          <div className="form-field">
            <label htmlFor="role">Role / title</label>
            <select id="role" value={role} onChange={(e) => setRole(e.target.value)} required>
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.value === ''}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="product">Product interest</label>
            <select id="product" value={productInterest} onChange={(e) => setProductInterest(e.target.value)} required>
              {PRODUCT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.value === ''}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="team-size">Team size</label>
            <select id="team-size" value={teamSize} onChange={(e) => setTeamSize(e.target.value)} required>
              {TEAM_SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.value === ''}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <label htmlFor="message">What are you trying to solve?</label>
        <textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="Briefly describe your current process and what success looks like."
          required
        />

        <button
          type="submit"
          disabled={
            submitting ||
            !name.trim() ||
            !email.trim() ||
            !company.trim() ||
            !role ||
            !productInterest ||
            !teamSize ||
            !message.trim()
          }
        >
          {submitting ? 'Sending…' : 'Request a demo'}
        </button>
      </form>
    </div>
  );
};

export default RequestDemo;
